import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../apiClient';

// Read-only view of the live SOW extraction prompt, plus instructions for changing it.
//
// Deliberately not editable in the browser. Jeremy's ask was that people be able to maintain as
// much as possible without opening files -- but the prompt is the one thing where the opposite is
// right, and it's worth being explicit about why: an AI Builder prompt edited live in a GUI was
// exactly what we just left behind. There was no diff, no review, and no way to tell which prompt
// produced a given historical extraction. Keeping it in git buys all three back. See
// docs/decisions/0005-claude-api-replaces-ai-builder.md.
//
// The page still earns its place: it shows what is *actually* running (read from the same file the
// extraction call reads, so it can't drift), and tells you exactly where to change it.
export function PromptTab() {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    apiFetch('/ai/prompt')
      .then(data => { if (!cancelled) setInfo(data); })
      .catch(err => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, []);

  return (
    <>
      {error && (
        <div className="card" style={{ borderLeft: '4px solid var(--red, #c0392b)' }}>
          <div className="card-title">Couldn't load the prompt</div>
          <p>{error}</p>
        </div>
      )}

      {!info && !error && <div className="card"><p>Loading…</p></div>}

      {info && (
        <>
          <p style={{ color: 'var(--text-mid)', marginBottom: 16 }}>
            This is the SOW extraction prompt specifically — Peter's help-center prompt is a
            separate file (<code>apps/api/src/ai/prompts/peter-chat.v4.md</code>) and isn't shown here.
          </p>

          <div className="card">
            <div className="card-title">Currently running</div>
            <div className="form-group">
              <strong>Prompt version:</strong> <span className="badge badge-blue">{info.version}</span>
            </div>
            <div className="form-group">
              <strong>Model:</strong> <span className="badge badge-green">{info.model}</span>
            </div>
            <div className="form-group">
              <strong>Provider:</strong>{' '}
              <span className="badge badge-yellow">
                {info.provider === 'foundry' ? 'Microsoft Foundry (Azure)' : 'Anthropic API'}
              </span>
              {info.provider === 'foundry' && (
                <span style={{ color: 'var(--text-mid)', marginLeft: '0.5rem' }}>
                  — billed through Azure. "Model" above is the Foundry deployment name.
                </span>
              )}
            </div>
            <div className="form-group">
              <strong>Source file:</strong> <code>{info.sourcePath}</code>
            </div>
            <p style={{ color: 'var(--text-mid)' }}>
              Every extraction is recorded against this version, so a past result can always be
              traced back to the exact instructions that produced it.
            </p>
          </div>

          <div className="card">
            <div className="card-title">How to change it</div>
            <p>
              The prompt lives in the codebase rather than in a settings screen, so that every
              change is reviewed and every past extraction stays traceable to the wording that
              produced it. To change it:
            </p>
            <ol style={{ lineHeight: 1.8, paddingLeft: '1.2rem' }}>
              <li>Open <code>{info.sourcePath}</code> in the repository.</li>
              <li>
                For a <strong>wording or typo fix</strong>, edit the file in place. For a change
                that alters <strong>what the AI actually returns</strong>, copy it to a new
                <code> v2 </code> file instead and update <code>PROMPT_VERSION</code> in
                <code> apps/api/src/ai/ai.service.ts</code> — that keeps older log entries pointing
                at a prompt that still exists.
              </li>
              <li>Open a pull request. Deploying the merge makes it live; there is no publish step.</li>
            </ol>
            <p style={{ color: 'var(--text-mid)' }}>
              Adding or removing a field is a bigger change than it looks — the field list also
              lives in the extraction schema and the review screen, and all three have to move
              together. The developer guide's <em>AI services</em> section spells this out.
            </p>
          </div>

          <div className="card">
            <div className="card-title">The prompt in full</div>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                background: 'var(--bg-alt, #f7f7f9)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                padding: '1rem',
                fontSize: '0.85rem',
                lineHeight: 1.6,
                maxHeight: '32rem',
                overflowY: 'auto',
              }}
            >
              {info.prompt}
            </pre>
          </div>
        </>
      )}
    </>
  );
}

// Under a cent reads as "$0.00", which looks like nothing happened. Show more decimals for the
// small numbers a help chatbot actually produces, and stop at cents once it's real money.
function formatUsd(amount) {
  if (amount === 0) return '$0.00';
  if (amount < 0.01) return `$${amount.toFixed(4)}`;
  return `$${amount.toFixed(2)}`;
}

// Per-user call counts, token totals, and estimated cost for Peter. Rows logged before the
// 2026-08-28 token-usage migration carry no usage figure -- shown as a count with a note rather than
// silently as zero, so an early adopter's real usage doesn't read as "nothing happened."
export function UsageTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setError('');
    return apiFetch('/ai/usage?module=peter')
      .then(setData)
      .catch(err => setError(err.message));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (error) return <div className="card" style={{ borderLeft: '4px solid var(--red, #c0392b)' }}><div className="card-title">Couldn't load usage</div><p>{error}</p></div>;
  if (!data) return <div className="card"><p>Loading…</p></div>;

  const anyPreCapture = data.byUser.some(r => r.callsWithoutTokenData > 0);
  const anyUnpriced = data.byUser.some(r => r.callsWithUnpricedModel > 0);

  const pct = v => (v == null ? '—' : `${v}%`);

  return (
    <>
      <div className="card">
        <div className="card-title">Peter — usage by month</div>
        <p style={{ color: 'var(--text-mid)', marginBottom: 16 }}>
          Search success rate: <strong>{pct(data.overallSearchSuccessRatePct)}</strong> of answers cited a help topic.
          Document coverage: <strong>{pct(data.overallDocumentCoveragePct)}</strong> of {data.corpusTopics} help topics cited at least once.
        </p>
        {data.byMonth.length === 0 ? (
          <p style={{ color: 'var(--text-mid)' }}>No calls yet.</p>
        ) : (
          <table className="sheet" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Month</th>
                <th style={{ textAlign: 'right' }}>Calls</th>
                <th style={{ textAlign: 'right' }}>Users</th>
                <th style={{ textAlign: 'right' }}>Input tokens</th>
                <th style={{ textAlign: 'right' }}>Output tokens</th>
                <th style={{ textAlign: 'right' }}>Est. cost</th>
                <th style={{ textAlign: 'right' }}>Search success</th>
                <th style={{ textAlign: 'right' }}>Doc coverage</th>
              </tr>
            </thead>
            <tbody>
              {[...data.byMonth].reverse().map(m => (
                <tr key={m.month}>
                  <td>{m.month}</td>
                  <td style={{ textAlign: 'right' }}>{m.calls}</td>
                  <td style={{ textAlign: 'right' }}>{m.users}</td>
                  <td style={{ textAlign: 'right' }}>{m.inputTokens.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{m.outputTokens.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{formatUsd(m.estimatedCostUsd)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {pct(m.searchSuccessRatePct)}
                    {m.callsWithCitationData > 0 && m.callsWithCitationData < m.calls && (
                      <span style={{ color: 'var(--text-mid)', fontSize: 11 }}> ({m.callsWithCitationData}/{m.calls} tracked)</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {pct(m.documentCoveragePct)}
                    {m.topicsCited > 0 && <span style={{ color: 'var(--text-mid)', fontSize: 11 }}> ({m.topicsCited} topics)</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <div className="card-title">Peter — usage by user</div>
        <p style={{ color: 'var(--text-mid)', marginBottom: 16 }}>
          {data.totalCalls} call{data.totalCalls === 1 ? '' : 's'} total —{' '}
          {data.totalInputTokens.toLocaleString()} input / {data.totalOutputTokens.toLocaleString()} output tokens,{' '}
          <strong>{formatUsd(data.totalEstimatedCostUsd)}</strong> estimated.
        </p>

        {data.byUser.length === 0 ? (
          <p style={{ color: 'var(--text-mid)' }}>No calls yet.</p>
        ) : (
          <table className="sheet" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>User</th>
                <th style={{ textAlign: 'right' }}>Calls</th>
                <th style={{ textAlign: 'right' }}>Input tokens</th>
                <th style={{ textAlign: 'right' }}>Output tokens</th>
                <th style={{ textAlign: 'right' }}>Est. cost</th>
                <th style={{ textAlign: 'left' }}>Model(s)</th>
              </tr>
            </thead>
            <tbody>
              {data.byUser.map(row => (
                <tr key={row.userId ?? '(no user)'}>
                  <td>{row.user ? `${row.user.name} <${row.user.email}>` : (row.userId ?? '(no user)')}</td>
                  <td style={{ textAlign: 'right' }}>
                    {row.calls}
                    {row.callsWithoutTokenData > 0 && (
                      <span style={{ color: 'var(--text-mid)', fontSize: 11 }}> ({row.callsWithoutTokenData} pre-capture)</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>{row.inputTokens.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>{row.outputTokens.toLocaleString()}</td>
                  <td style={{ textAlign: 'right' }}>
                    {formatUsd(row.estimatedCostUsd)}
                    {row.callsWithUnpricedModel > 0 && (
                      <span style={{ color: 'var(--text-mid)', fontSize: 11 }}> +{row.callsWithUnpricedModel} unpriced</span>
                    )}
                  </td>
                  <td>{row.models.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <div className="card-title">About these numbers</div>
        <ul style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.7, paddingLeft: 18, margin: 0 }}>
          <li>
            <strong>Estimated cost is calculated here</strong>, from the tokens recorded on each call
            times Anthropic's published per-model rates. Claude on Microsoft Foundry bills through the
            Microsoft Marketplace at those same rates, so the estimate holds under either provider.
          </li>
          <li>
            <strong>For the invoiced figure, use Azure Cost Management</strong> (Azure portal → Cost
            Management + Billing → Cost analysis, filtered to the Foundry resource). That's the
            billing system of record; this page can't see credits, discounts, or your agreement's terms.
          </li>
          <li>
            <strong>Search success rate</strong> is the share of answers that cited a help topic. Data
            questions answered from live numbers cite none by design, so they're left out of the rate.{' '}
            <strong>Document coverage</strong> is the share of help topics cited at least once in the
            month. Earlier months were backfilled on 2026-11-03 from the logged answers.
          </li>
          {anyPreCapture && (
            <li>
              <strong>Pre-capture calls</strong> were made before token recording was added on
              2026-08-28. They're real calls that cost real money — there's just no token figure to
              price them with, so they're excluded from the estimate rather than counted as zero.
            </li>
          )}
          {anyUnpriced && (
            <li>
              <strong>Unpriced calls</strong> ran on a model with no published rate in the app's
              pricing table — usually a Foundry deployment name that doesn't match a known model id.
              Add it to <code>apps/api/src/ai/model-pricing.constants.ts</code> to price them.
            </li>
          )}
        </ul>
      </div>
    </>
  );
}

// Admin-editable overrides for Peter's daily message cap and model. Blank means "no override" --
// Peter falls back to its env vars, then a hardcoded default (peter.service.ts). "Currently in
// effect" is shown separately from the override fields because a blank field doesn't mean "no
// limit"; it means "whatever the env var/default says," which is worth showing rather than leaving
// the admin to guess.
export function LimitsTab() {
  const [effective, setEffective] = useState(null);
  const [cap, setCap] = useState('');
  const [model, setModel] = useState('');
  const [updatedBy, setUpdatedBy] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const [settings, peterStatus] = await Promise.all([
        apiFetch('/ai/settings'),
        apiFetch('/ai/peter'),
      ]);
      setCap(settings.peterDailyMessageCap != null ? String(settings.peterDailyMessageCap) : '');
      setModel(settings.peterModel || '');
      setUpdatedBy(settings.updatedBy ? { ...settings.updatedBy, at: settings.updatedAt } : null);
      setEffective(peterStatus);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSave() {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      // Omitted (not null) when blank -- JSON.stringify drops an `undefined` key on its own, and
      // the API's `dto.field ?? null` treats "omitted" and "explicit null" the same way anyway.
      // Matches Maintenance.js's convention for clearing an optional field.
      const dto = {
        peterDailyMessageCap: cap.trim() ? Number(cap) : undefined,
        peterModel: model.trim() || undefined,
      };
      await apiFetch('/ai/settings', { method: 'PATCH', body: JSON.stringify(dto) });
      await load();
      setSaved(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (!loaded) return <div className="card"><p>Loading…</p></div>;

  return (
    <div className="card">
      <div className="card-title">Peter — limits</div>

      {error && <div className="notice notice-error">⚠ {error}</div>}

      {effective && (
        <p style={{ color: 'var(--text-mid)', marginBottom: 16 }}>
          Currently in effect: <span className="badge badge-blue">{effective.dailyMessageCap} questions/day</span>{' '}
          <span className="badge badge-green">{effective.model}</span>
        </p>
      )}

      <div className="form-group">
        <label className="form-label">Daily message cap override</label>
        <input
          className="form-input"
          type="number"
          min={1}
          max={1000}
          placeholder="e.g. 100 — blank uses PETER_DAILY_MESSAGE_CAP or the default"
          value={cap}
          onChange={e => setCap(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <div style={{ fontSize: 11, color: 'var(--text-mid)', marginTop: 4 }}>
          Questions per person per day. A runaway guard, not a spend budget — blank falls back to
          the <code>PETER_DAILY_MESSAGE_CAP</code> env var, then 100.
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Model override</label>
        <input
          className="form-input"
          type="text"
          placeholder="e.g. claude-sonnet-5 — blank uses PETER_MODEL or the AI provider's default"
          value={model}
          onChange={e => setModel(e.target.value)}
          style={{ maxWidth: 320 }}
        />
        <div style={{ fontSize: 11, color: 'var(--text-mid)', marginTop: 4 }}>
          On Foundry this is a deployment name, not a model id. Blank falls back to{' '}
          <code>PETER_MODEL</code>, then whatever the AI provider is configured with.
        </div>
      </div>

      {updatedBy && (
        <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 16 }}>
          Last changed by {updatedBy.name} ({updatedBy.email})
          {updatedBy.at && ` on ${new Date(updatedBy.at).toLocaleString()}`}
        </div>
      )}

      <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
        {saving ? 'Saving…' : 'Save'}
      </button>
      {saved && !saving && <span style={{ marginLeft: 12, color: 'var(--green)', fontSize: 13 }}>Saved.</span>}
    </div>
  );
}

// Administration.js's tree nav renders each of the three Tab functions above directly (imported by
// name) rather than through a wrapper page component -- it owns the section/sub-tab labels and
// selection itself now, since the tree needs both levels' labels to draw the whole thing at once.
// Every /ai/* route these call except peter/chat is behind @Roles('admin') on the API (tightened
// 2026-08-28; it had no guard before) -- that's still the real boundary either way.
